import { useClientPortalAPI } from "@certn/api";
import {
  BasicButton,
  Dialog,
  DialogContent,
  DialogHeader,
  Loader,
  Modal,
  Search,
  SectionMessage,
  Text,
} from "@certn/cascadia-react";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { OrderingCheckCard } from "../../components/ordering-check-card";
import { CheckData } from "./types";

interface PackageEditAddCheckModalProps {
  selectedCheckTypes: string[];
  isApplicantPay?: boolean;
  onAdd: (check: CheckData) => void;
  onClose: () => void;
}

export function PackageEditAddCheckModal({
  selectedCheckTypes,
  isApplicantPay,
  onAdd,
  onClose,
}: PackageEditAddCheckModalProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const {
    data: availableChecks,
    isLoading,
    error,
  } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/available-checks/`,
    isApplicantPay ? { is_applicant_pay: "true" } : undefined
  );

  const matchingChecks = useMemo(() => {
    if (!availableChecks) {
      return [];
    }

    const selected = new Set(selectedCheckTypes);
    const unselected = availableChecks.filter(
      (check: CheckData) => !selected.has(check.type)
    );
    const searchTerm = search.trim().toLowerCase();

    if (!searchTerm) {
      return [];
    }

    return unselected.filter((check: CheckData) =>
      check.name.toLowerCase().includes(searchTerm)
    );
  }, [availableChecks, search, selectedCheckTypes]);

  return (
    <Modal
      isOpen
      testID="package-edit-add-check-modal"
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Dialog>
        <DialogHeader onClose={onClose}>{t("Add check")}</DialogHeader>
        <DialogContent>
          <div className={styles.content}>
            <Search
              testID="package-edit-check-search"
              placeholder={t("Search checks...")}
              value={search}
              onChange={setSearch}
              aria-label={t("Search checks...")}
            />
            {error ? (
              <SectionMessage type="danger" icon="error">
                {String(error.message)}
              </SectionMessage>
            ) : isLoading ? (
              <Loader />
            ) : matchingChecks.length === 0 ? (
              <Text style="form/body-secondary">
                {search.trim()
                  ? t("No checks match your search.")
                  : t("Search for a check to add to this package.")}
              </Text>
            ) : (
              <div className={styles.cards}>
                {matchingChecks.map((check: CheckData) => (
                  <OrderingCheckCard
                    key={check.type}
                    type={check.type}
                    name={check.name}
                    price={check.price}
                    priceIsCatalogMinimum={check.price_is_catalog_minimum}
                    currency={check.currency}
                    checkChildren={check.children || []}
                    turnaroundTime={check.estimated_turnaround_time}
                    disclaimersIconList={check.disclaimers_icon_list}
                    disclaimers={check.disclaimers}
                    description={check.description}
                    infoParagraphs={check.info_paragraphs}
                    coverage={check.coverage ?? ""}
                    keyFeatures={check.key_features}
                    howItCanHelpYou={check.how_it_can_help_you}
                    selectFieldDescription={check.select_field_descriptions}
                    selectFieldEndpoint={check.select_field_endpoint}
                    selectOptions={check.select_options}
                    umbrellaChecksSupported={check.children || []}
                    umbrellaChecksHeading={check.umbrella_child_checks_heading}
                    isSelected={false}
                    onPress={() => onAdd(check)}
                    testID={slugify(check.type)}
                    width={480}
                    selectFieldInformation={check.select_field_information}
                    addOnOptions={check.add_on_options}
                    checkOrderingError={
                      check.check_has_ordering_error_for_customer_group
                    }
                  />
                ))}
              </div>
            )}
            <BasicButton
              onPress={onClose}
              testID="package-edit-add-check-close"
            >
              {t("Close")}
            </BasicButton>
          </div>
        </DialogContent>
      </Dialog>
    </Modal>
  );
}

const styles = {
  content: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
    maxHeight: "70vh",
    overflowY: "auto",
  }),
  cards: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
  }),
};
